import React, { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Download, Maximize2 } from "lucide-react";
import { SLIDES, UI } from "./data";
import SlideView from "./SlideView";
import "./deck.css";

export default function Deck() {
  const [index, setIndex] = useState(0);
  const [lang, setLang] = useState("it");
  const [dir, setDir] = useState(1);
  const total = SLIDES.length;
  const ui = UI[lang];

  const go = useCallback(
    (n) => {
      setDir(n > index ? 1 : -1);
      setIndex(Math.max(0, Math.min(total - 1, n)));
    },
    [index, total]
  );

  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        go(0);
      } else if (e.key === "End") {
        go(total - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go, total]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const exportPdf = () => window.print();

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -60 : 60 }),
  };

  return (
    <div
      className={`deck-root ${lang === "ar" ? "lang-ar" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
      data-testid="deck-root"
    >
      {/* Top bar */}
      <header className="deck-bar">
        <div className="deck-brand">
          <span className="deck-brand-mark">SL</span>
          <span className="deck-brand-name">San Leone</span>
        </div>
        <div className="deck-bar-actions">
          <div className="deck-lang" data-testid="lang-toggle">
            <button
              className={lang === "it" ? "on" : ""}
              onClick={() => setLang("it")}
              data-testid="lang-it"
            >
              IT
            </button>
            <button
              className={lang === "en" ? "on" : ""}
              onClick={() => setLang("en")}
              data-testid="lang-en"
            >
              EN
            </button>
            <button
              className={lang === "ar" ? "on" : ""}
              onClick={() => setLang("ar")}
              data-testid="lang-ar"
            >
              AR
            </button>
          </div>
          <button className="deck-iconbtn" onClick={toggleFullscreen} title="Fullscreen">
            <Maximize2 size={16} />
          </button>
          <button
            className="deck-iconbtn deck-export"
            onClick={exportPdf}
            data-testid="export-pdf"
            title={ui.export}
          >
            <Download size={16} />
            <span>{ui.export}</span>
          </button>
        </div>
      </header>

      {/* Stage */}
      <div className="deck-stage">
        <div className="deck-frame">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={index}
              className="deck-slide-wrap"
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <SlideView slide={SLIDES[index]} lang={lang} active />
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          className="deck-nav deck-nav-prev"
          onClick={prev}
          disabled={index === 0}
          data-testid="nav-prev"
          aria-label={ui.prev}
        >
          <ChevronLeft size={26} />
        </button>
        <button
          className="deck-nav deck-nav-next"
          onClick={next}
          disabled={index === total - 1}
          data-testid="nav-next"
          aria-label={ui.next}
        >
          <ChevronRight size={26} />
        </button>
      </div>

      {/* Bottom controls */}
      <footer className="deck-footer">
        <div className="deck-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              className={`deck-dot ${i === index ? "on" : ""}`}
              onClick={() => go(i)}
              data-testid={`dot-${i}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <div className="deck-counter" data-testid="slide-counter">
          {ui.slideOf(index + 1, total)}
        </div>
      </footer>

      {/* PRINT-ONLY: all slides stacked for PDF export */}
      <div className="deck-print" data-testid="deck-print">
        {SLIDES.map((s) => (
          <div key={s.id} className="deck-print-page">
            <SlideView slide={s} lang={lang} active />
          </div>
        ))}
      </div>
    </div>
  );
}
